import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2, MessageSquare } from "lucide-react";
import PhoneField from "@/components/auth/PhoneField";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { setToken, isAppBuild } from "@/lib/net";
import { Link } from "wouter";
import {
  classifyVerifyResponse, emailFallbackMessage, isEmailFallbackText, chooserRoleLabel,
  type ChooserAccount,
} from "./otpFlow";

interface OTPLoginProps {
  onSuccess: (token: string, user: any) => void;
  /** Omitted in the mobile app (OTP is its only sign-in) — the Back button is then hidden. */
  onBack?: () => void;
}

export function OTPLogin({ onSuccess, onBack }: OTPLoginProps) {
  const [step, setStep] = useState<'phone' | 'otp' | 'choose'>('phone');
  const [choiceToken, setChoiceToken] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<ChooserAccount[]>([]);
  const app = isAppBuild();
  /** Server/network text for a toast — never tell an app user to use email sign-in (the app hides it). */
  const shown = (message: string | undefined, fallback: string) =>
    app && isEmailFallbackText(message) ? emailFallbackMessage(true) : (message || fallback);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messageId, setMessageId] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const { toast } = useToast();


  const sendOTP = async () => {
    if (!phone) {
      toast({
        title: "Error",
        description: "Please enter your phone number",
        variant: "destructive",
      });
      return;
    }

    // Validate phone number format
    if (!phone.startsWith('+') || phone.length < 8) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid international phone number starting with +",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiRequest('/api/auth/send-otp', {
        method: 'POST',
        data: { phone }
      });

      const data = await response.json();

      // Handle MFA-enabled users - redirect to email login
      if (data.mfaRequired) {
        toast({
          title: app ? "Can't sign in here yet" : "Email Login Required",
          description: emailFallbackMessage(app),
          variant: "destructive",
        });
        onBack?.(); // Return to login options
        return;
      }

      if (data.success) {
        setMessageId(data.messageId);
        setOtpSent(true);
        setStep('otp');

        const deliveryMethod = data.method || 'WhatsApp';
        toast({
          title: "OTP Sent",
          description: `Check your ${deliveryMethod} for the verification code`,
        });
      } else {
        // Handle WhatsApp Business API limitations
        const isWhatsAppIssue = data.message?.includes('WhatsApp') || data.message?.includes('24-hour');

        toast({
          title: isWhatsAppIssue ? "WhatsApp Delivery Issue" : "Error",
          description: data.message || "Failed to send OTP",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: shown(error.message, "Failed to send OTP. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 4) {
      toast({
        title: "Error",
        description: "Please enter the 4-digit verification code",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiRequest('/api/auth/verify-otp', {
        method: 'POST',
        data: { phone, otp }
      });

      const data = await response.json();
      const outcome = classifyVerifyResponse(data);

      // Handle MFA-enabled users - redirect to email login (web) / explain (app)
      if (outcome.kind === "mfa") {
        toast({
          title: app ? "Can't sign in here yet" : "Email Login Required",
          description: emailFallbackMessage(app, outcome.message),
          variant: "destructive",
        });
        onBack?.(); // Return to login options
        return;
      }

      // Several accounts share this phone: the server mints the token only after a pick.
      if (outcome.kind === "choose") {
        setChoiceToken(outcome.choiceToken);
        setAccounts(outcome.accounts);
        setStep('choose');
        return;
      }

      if (outcome.kind === "signed_in") {
        completeSignIn(outcome);
      } else {
        toast({
          title: "Error",
          description: shown(outcome.message, "Invalid verification code"),
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: shown(error.message, "Failed to verify OTP. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /** Only ever called with a real token (classifyVerifyResponse never yields signed_in without one). */
  const completeSignIn = (data: { token: string; user: any }) => {
    setToken(data.token);

    // Invalidate all queries to ensure fresh data after login
    queryClient.invalidateQueries();

    toast({
      title: "Success",
      description: "Login successful!",
    });
    onSuccess(data.token, data.user);
  };

  /** Back to the start: the choiceToken is single-use, so any failed pick needs a fresh OTP. */
  const restart = () => {
    setStep('phone');
    setOtp('');
    setOtpSent(false);
    setMessageId(null);
    setChoiceToken(null);
    setAccounts([]);
  };

  const chooseAccount = async (userId: number) => {
    if (!choiceToken) { restart(); return; }
    setIsLoading(true);
    const token = choiceToken;
    setChoiceToken(null); // single-use: never re-post it
    try {
      const response = await apiRequest('/api/auth/verify-otp/choose', {
        method: 'POST',
        data: { choiceToken: token, userId }
      });
      const outcome = classifyVerifyResponse(await response.json());
      if (outcome.kind === "signed_in") {
        completeSignIn(outcome);
        return;
      }
      toast({
        title: "Error",
        description: outcome.kind === "failed" ? outcome.message : "Please sign in again.",
        variant: "destructive",
      });
      restart();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "This selection has expired. Please sign in again.",
        variant: "destructive",
      });
      restart();
    } finally {
      setIsLoading(false);
    }
  };

  const resendOTP = async () => {
    setOtp('');
    await sendOTP();
  };


  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setOtp(value);
  };

  return (
    <Card className="w-full max-w-md mx-auto bg-black border-gray-700">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-yellow-600 rounded-lg">
            <MessageSquare className="h-8 w-8 text-black" />
          </div>
        </div>
        <CardTitle className="text-2xl font-bold text-yellow-600">
          {step === 'phone' ? 'Login with WhatsApp' : step === 'choose' ? 'Choose Your Account' : 'Enter Verification Code'}
        </CardTitle>
        <CardDescription className="text-gray-300">
          {step === 'phone' 
            ? 'Enter your phone number to receive a verification code via WhatsApp' 
            : step === 'choose'
            ? 'More than one account uses this number. Which one would you like to open?'
            : 'Enter the 4-digit code sent to your WhatsApp'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {step === 'choose' ? (
          <div className="space-y-3">
            {accounts.map((a) => (
              <Button
                key={a.id}
                variant="outline"
                onClick={() => chooseAccount(a.id)}
                disabled={isLoading}
                className="w-full h-auto py-3 flex flex-col items-start border-gray-600 text-white hover:border-yellow-600 hover:bg-yellow-600/10"
              >
                <span className="font-semibold text-yellow-600">{chooserRoleLabel(a.role)}</span>
                {a.maskedEmail && <span className="text-xs text-gray-300">{a.maskedEmail}</span>}
              </Button>
            ))}
            {isLoading && (
              <div className="flex justify-center text-gray-300 text-sm">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...
              </div>
            )}
            <Button
              variant="outline"
              onClick={restart}
              disabled={isLoading}
              className="w-full border-yellow-600 text-yellow-600 hover:bg-yellow-600 hover:text-black"
            >
              Use a Different Number
            </Button>
          </div>
        ) : step === 'phone' ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-yellow-600">Phone Number</Label>
              <PhoneField id="phone" value={phone} onChange={setPhone} disabled={isLoading} />
              <p className="text-xs text-gray-400">
                Choose your country, then type your mobile number. You'll get a code on WhatsApp.
              </p>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={sendOTP} 
                disabled={isLoading || !phone}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-black font-semibold"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  <>
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Send Verification Code
                  </>
                )}
              </Button>

              {onBack && (
              <Button 
                variant="outline" 
                onClick={onBack}
                className="w-full border-yellow-600 text-yellow-600 hover:bg-yellow-600 hover:text-black"
                disabled={isLoading}
              >
                Back to Login Options
              </Button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="otp" className="text-yellow-600">Verification Code</Label>
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                placeholder="1234"
                value={otp}
                onChange={handleOtpChange}
                className="text-center text-2xl font-bold tracking-widest bg-black border-gray-600 text-white placeholder-white focus:border-yellow-600"
                maxLength={4}
                disabled={isLoading}
              />
              <p className="text-xs text-gray-400 text-center">
                Code sent to {phone}
                {messageId && (
                  <span className="block text-xs text-yellow-600 mt-1">
                    Message ID: {messageId.slice(-8)}
                  </span>
                )}
              </p>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={verifyOTP} 
                disabled={isLoading || otp.length !== 4}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-black font-semibold"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Login'
                )}
              </Button>

              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  onClick={resendOTP}
                  disabled={isLoading}
                  className="flex-1 border-yellow-600 text-yellow-600 hover:bg-yellow-600 hover:text-black"
                >
                  Resend Code
                </Button>

                <Button 
                  variant="outline" 
                  onClick={() => {
                    setStep('phone');
                    setOtp('');
                    setOtpSent(false);
                    setMessageId(null);
                  }}
                  disabled={isLoading}
                  className="flex-1 border-yellow-600 text-yellow-600 hover:bg-yellow-600 hover:text-black"
                >
                  Change Number
                </Button>
              </div>

              {/* Support fallback for delivery issues */}
              <div className="mt-4 p-3 bg-red-600/10 border border-red-600/20 rounded-lg">
                <h4 className="text-sm font-medium text-red-400 mb-2">Not receiving the code?</h4>
                <p className="text-xs text-gray-300 mb-2">
                  WhatsApp Business API requires contact verification for international delivery.
                </p>
                <p className="text-xs text-yellow-400">
                  Contact support via WhatsApp: +91 94467 76666 for immediate assistance
                </p>
              </div>
            </div>
          </>
        )}

        {app ? (
          // Store app (Task C4): crew and managers have their own sign-in screens.
          <div className="flex justify-between text-sm pt-2" data-testid="app-staff-links">
            <Link href="/login/crew" className="text-yellow-600 underline">Crew &amp; staff</Link>
            <Link href="/login/staff" className="text-yellow-600 underline">Managers</Link>
          </div>
        ) : (
          // Web (audit CREW-3): crew sign in with a WhatsApp code here too; managers use the
          // email + password form already on this page.
          <div className="flex justify-center text-sm pt-2" data-testid="web-crew-link">
            <Link href="/login/crew" className="text-yellow-600 underline">Crew &amp; staff</Link>
          </div>
        )}

        <div className="text-center pt-4 border-t border-gray-600">
          <p className="text-xs text-gray-400">
            Secure login powered by WhatsApp Cloud API
          </p>
        </div>
      </CardContent>
    </Card>
  );
}